import React, { useState, useEffect, useRef, useMemo } from "react";
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Dialog, 
  DialogContent, 
  IconButton 
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useSnackbar } from 'notistack';
import { enqueueUniqueSnackbar } from '../../utilities/snackbarUtils';
import { getVideoStreamingUrl } from '../../utilities/temporaryUrlUtils';
import { getMediaType } from '../../utilities/mediaUtils';
import { isBrowserPlayableVideo } from '../../utilities/mediaTypes';
import { MediaResponse, MediaItemDetailed } from '../../types';

// Import our renderers
import VideoRenderer from './renderers/VideoRenderer';
import AudioRenderer from './renderers/AudioRenderer';
import PDFRenderer from './renderers/PDFRenderer';
import ImageRenderer from './renderers/ImageRenderer';

interface MediaViewerProps {
  // Media sources (provide one of these)
  item?: MediaResponse | MediaItemDetailed | null;
  url?: string;
  file?: File;
  filename?: string;
  
  // Display options
  mode?: 'preview' | 'thumbnail' | 'dialog';
  width?: string | number;
  height?: string | number;
  
  // Dialog specific props
  open?: boolean;
  onClose?: () => void;
  
  // Callbacks
  onClick?: () => void;
  onLoad?: () => void;
  onError?: () => void;
}

export const MediaViewer: React.FC<MediaViewerProps> = React.memo(({
  item,
  url,
  file,
  filename,
  mode = 'preview',
  width = "100%",
  height = "100%",
  open,
  onClose,
  onClick,
  onLoad,
  onError,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [streamingResult, setStreamingResult] = useState<{ mode: string; tested: boolean } | null>(null);
  
  // Image zoom functionality
  const [zoomControls, setZoomControls] = useState<{ zoomIn: () => void; zoomOut: () => void; resetTransform: () => void } | null>(null);

  // MEMOIZED: Calculate the media URL and filename to prevent infinite re-renders
  const { mediaUrl, mediaFilename } = useMemo(() => {
    let calculatedUrl = '';
    let calculatedFilename = filename || '';
    
    // Priority order: url prop > file prop > item properties
    if (url) {
      // If URL is provided, use it directly - DON'T create additional blob URLs
      calculatedUrl = url;
      if (file) {
        calculatedFilename = calculatedFilename || file.name;
      }
    } else if (file) {
      // Only create blob URL if no explicit URL was provided
      calculatedUrl = URL.createObjectURL(file);
      calculatedFilename = calculatedFilename || file.name;
    } else if (item) {
      // Try different URL sources from the item
      calculatedUrl = item.url || 
                     (item as any).remote_details?.url || 
                     (item as any).previewUrl || 
                     '';
                     
      // FALLBACK: If no URL found but we have an ID, try to construct a URL from the API
      if (!calculatedUrl && (item as any).id) {
        const itemId = (item as any).id;
        calculatedUrl = `${process.env.REACT_APP_API_ROOT}api/user/media/${itemId}/file/`;
        console.log('[MediaViewer] Constructed fallback URL from ID:', calculatedUrl);
      }
      
      calculatedFilename = calculatedFilename || 
                          (item as any).original_file?.filename || 
                          (calculatedUrl ? calculatedUrl.split('/').pop() : '') ||
                          (item as any).name || 
                          '';
    }
    
    // DEBUG: Log the URL resolution process
    console.log('[MediaViewer] URL Resolution Debug:', {
      hasUrl: !!url,
      hasFile: !!file,
      hasItem: !!item,
      itemUrl: item?.url,
      itemRemoteDetailsUrl: (item as any)?.remote_details?.url,
      itemPreviewUrl: (item as any)?.previewUrl,
      calculatedUrl,
      calculatedFilename
    });
    
    return {
      mediaUrl: calculatedUrl,
      mediaFilename: calculatedFilename
    };
  }, [url, file, item, filename]);

  // Reset component state when media URL changes (new media item selected)
  useEffect(() => {
    if (mediaUrl) {
      setIsLoading(true);
      setLoadError(null);
      setResolvedUrl(null);
      setStreamingResult(null);
    }
  }, [mediaUrl]);

  // Cleanup blob URL when component unmounts or file changes
  // Only cleanup if we created the blob URL ourselves (when no url prop was provided)
  useEffect(() => {
    return () => {
      if (!url && file && mediaUrl && mediaUrl.startsWith('blob:')) {
        URL.revokeObjectURL(mediaUrl);
      }
    };
  }, [url, file, mediaUrl]);

  // IMPROVED: Use media_type field from item if available, otherwise detect from filename/URL
  let mediaType = 'unknown';
  if (item && (item as any).media_type) {
    // Convert both MIME types and Fulcrum-style types to our media type format
    const itemMediaType = (item as any).media_type.toLowerCase();
    
    // Handle MIME types (image/jpeg, video/mp4, etc.)
    if (itemMediaType.startsWith('image/')) {
      mediaType = 'image';
    } else if (itemMediaType.startsWith('video/')) {
      mediaType = 'video';
    } else if (itemMediaType.startsWith('audio/')) {
      mediaType = 'audio';
    } else if (itemMediaType === 'application/pdf') {
      mediaType = 'pdf';
    }
    // Handle Fulcrum-style types (photo, video, audio, etc.)
    else if (itemMediaType === 'photo') {
      mediaType = 'image';
    } else if (itemMediaType === 'video') {
      mediaType = 'video';
    } else if (itemMediaType === 'audio') {
      mediaType = 'audio';
    } else if (itemMediaType === 'pdf' || itemMediaType === 'document') {
      mediaType = 'pdf';
    }
    
    console.log('[MediaViewer] Using media_type from item:', itemMediaType, '→', mediaType);
  } else {
    // Fallback to filename/URL detection
    mediaType = getMediaType(mediaFilename || mediaUrl);
    console.log('[MediaViewer] Using filename/URL detection:', mediaFilename || mediaUrl, '→', mediaType);
  }
  
  const isVideo = mediaType === 'video';
  const isAudio = mediaType === 'audio';
  const isImage = mediaType === 'image';
  const isPdf = mediaType === 'pdf';

  // Check if video needs server processing (e.g., MTS files)
  const needsServerProcessing = isVideo && mediaFilename && !isBrowserPlayableVideo(mediaFilename);

  console.log('[MediaViewer] UNIFIED - Type:', mediaType, 'URL:', mediaUrl, 'Mode:', mode);
  console.log('[MediaViewer] Media flags:', { isVideo, isAudio, isImage, isPdf });
  console.log('[MediaViewer] State:', { isLoading, loadError, resolvedUrl });


  // URL resolution for videos (using temporaryUrlUtils for complex streaming logic)
  useEffect(() => {
    if (!mediaUrl) {
      setIsLoading(false);
      setResolvedUrl(null);
      return;
    }

    if (isVideo) {
      setIsLoading(true);
      getVideoStreamingUrl(mediaUrl)
        .then(result => {
          console.log('[MediaViewer] Video URL resolved:', result);
          setResolvedUrl(result.url);
          setStreamingResult({ mode: result.mode, tested: result.tested });
          setIsLoading(false);
        })
        .catch(error => {
          console.error('[MediaViewer] Video URL resolution failed:', error);
          setResolvedUrl(mediaUrl);
          setStreamingResult({ mode: 'fallback', tested: false });
          setIsLoading(false);
        });
    } else {
      // For non-video media, use URL directly
      setResolvedUrl(mediaUrl);
      setIsLoading(false);
      console.log('[MediaViewer] Non-video URL resolved directly:', mediaUrl);
    }
  }, [mediaUrl, isVideo]);

  // Handle load/error for media
  const handleLoad = () => {
    setIsLoading(false);
    setLoadError(null); // Clear any previous errors on successful load
    if (onLoad) onLoad();
  };

  const handleError = (error?: any) => {
    setIsLoading(false);
    
    // Provide more specific error messages based on media type
    let errorMessage = 'Failed to load media';
    
    if (isVideo && error) {
      errorMessage = 'Unable to load video file. CMAF streaming may not be available for this video.';
    } else if (isAudio && error) {
      errorMessage = 'Unable to load audio file. Please try again or contact support if the issue persists.';
    } else if (isPdf && error) {
      if (error.message && error.message.includes('403')) {
        errorMessage = 'PDF access denied. This file may require authentication.';
      } else if (error.message && error.message.includes('CORS')) {
        errorMessage = 'PDF blocked by CORS policy.';
      } else {
        errorMessage = 'Unable to load PDF file. Please try again or contact support if the issue persists.';
      }
    } else if (isImage && error) {
      // Enhanced error handling for Fulcrum photos
      if (error.message && (error.message.includes('Fulcrum') || error.message.includes('API key'))) {
        errorMessage = error.message; // Use the detailed error from useFulcrumPhoto
      } else if (error.message && error.message.includes('403')) {
        errorMessage = 'Image access denied. This may be a Fulcrum photo requiring authentication.';
      } else if (error.message && error.message.includes('NetworkError')) {
        errorMessage = 'Network error loading image. Please check your connection and try again.';
      } else {
        errorMessage = 'Unable to load image file. Please try again or contact support if the issue persists.';
      }
    } else if (error) {
      errorMessage = 'Unable to load document file. Please try again or contact support if the issue persists.';
    }
    
    console.error('[MediaViewer] Media error:', error);
    setLoadError(errorMessage);
    
    // Show snackbar for all media errors
    enqueueUniqueSnackbar(enqueueSnackbar, errorMessage, {
      variant: 'error',
      autoHideDuration: 5000
    });
    
    if (onError) onError();
  };

  // Render the appropriate media renderer
  const renderMediaContent = () => {
    console.log('[MediaViewer] renderMediaContent called - Loading:', isLoading, 'Error:', loadError, 'URL:', resolvedUrl);
    
    // Show loading state if loading
    if (isLoading) { 
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          minHeight: '200px',
          gap: 2
        }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {isVideo ? 'Loading video...' : 
             isAudio ? 'Loading audio...' : 
             isPdf ? 'Loading PDF...' : 
             isImage ? 'Loading image...' : 
             'Loading media...'}
          </Typography>
        </Box>
      );
    }

    if (loadError) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography color="error">{loadError}</Typography>
        </Box>
      );
    }

    // IMPROVED: Check if we have no URL at all (not just no resolvedUrl)
    if (!resolvedUrl && !file && !mediaUrl) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 2 }}>
          <Typography color="error">No media URL available</Typography>
          <Typography variant="caption" color="text.secondary">
            The media item doesn't have a valid URL or file source
          </Typography>
          {item && (
            <Typography variant="caption" color="text.secondary">
              Item ID: {(item as any).id}, Name: {(item as any).name}
            </Typography>
          )}
        </Box>
      );
    }

    // Show server processing warning ONLY when:
    // 1. Video format needs server processing (like MTS files)
    // 2. AND streaming resolution failed (no m3u8 found)
    // 3. AND original format is not browser-playable
    const shouldShowServerProcessingWarning = isVideo && 
                                               mediaFilename && 
                                               !isBrowserPlayableVideo(mediaFilename) &&
                                               streamingResult?.mode === 'fallback' &&
                                               streamingResult?.tested;
    
    if (shouldShowServerProcessingWarning) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          padding: 2,
          textAlign: 'center'
        }}>
          <Typography variant="body1" color="warning.main" gutterBottom>
            📹 Server Processing Required
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This format file will need to be processed on the server to play it
          </Typography>
        </Box>
      );
    }

    // Handle unknown media types
    if (!isVideo && !isAudio && !isImage && !isPdf) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography color="text.secondary">
            Unsupported media type: {mediaType || 'unknown'}
          </Typography>
        </Box>
      );
    }

    if (isVideo && !isLoading && (resolvedUrl || file)) {
      // IMPROVED: Only render video if we're not loading and have a valid URL or file
      return (
        <VideoRenderer
          url={resolvedUrl || undefined}
          file={file}
          filename={mediaFilename}
          mode={mode}
          width={width}
          height={height}
          onLoad={handleLoad}
          onError={handleError}
          onClick={onClick}
        />
      );
    }

    if (isAudio && !isLoading && (resolvedUrl || file)) {
      return (
        <AudioRenderer
          url={resolvedUrl || undefined}
          file={file}
          filename={mediaFilename}
          mode={mode}
          width={width}
          height={height}
          onLoad={handleLoad}
          onError={handleError}
          onClick={onClick}
        />
      );
    }

    if (isPdf && !isLoading && (resolvedUrl || file)) {
      return (
        <PDFRenderer
          url={resolvedUrl || undefined}
          file={file}
          filename={mediaFilename}
          mode={mode}
          width={width}
          height={height}
          onLoad={handleLoad}
          onError={handleError}
          onClick={onClick}
        />
      );
    }

    if (isImage && !isLoading && (resolvedUrl || file)) {
      return (
        <ImageRenderer
          url={resolvedUrl || undefined}
          file={file}
          filename={mediaFilename}
          mode={mode}
          width={width}
          height={height}
          onLoad={handleLoad}
          onError={handleError}
          onClick={onClick}
          onZoomControlsChange={setZoomControls}
        />
      );
    }

    // Fallback for when no media is available
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography color="text.secondary">No media available</Typography>
      </Box>
    );
  };

  // Dialog mode - render in modal
  if (mode === 'dialog') {
    // FIXED: Always show dialog when open=true, regardless of content readiness
    // This allows us to show loading states and error messages properly
    return (
      <Dialog
        open={Boolean(open)}
        onClose={onClose}
        maxWidth={false}
        fullWidth={false}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.88)', // Increased backdrop opacity for better focus
          }
        }}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(18, 18, 18, 1)', // Slightly increased paper opacity
            // IMPROVED: Only use video-specific sizing when video is actually ready to render
            maxWidth: (isVideo && !isLoading && resolvedUrl) ? '95vw' : '90vw',
            minWidth: (isVideo && !isLoading && resolvedUrl) ? '80vw' : '600px',
            height: 'auto',
            maxHeight: '90vh',
            // IMPROVED: Ensure minimum height during loading to prevent layout shifts
            minHeight: isLoading ? '200px' : 'auto',
          }
        }}
        TransitionProps={{
          timeout: 300,
        }}
      >
        {/* Floating overlay controls */}
        <Box sx={{ 
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1000,
          display: 'flex',
          gap: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(12px)',
          borderRadius: 3,
          padding: 1,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          '& .MuiIconButton-root': {
            color: 'white',
            borderRadius: 2,
            transition: 'all 0.2s ease-out',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.1)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            }
          }
        }}>
          {isImage && zoomControls && (
            <>
              <IconButton onClick={zoomControls.zoomOut} size="small">
                <ZoomOutIcon />
              </IconButton>
              <IconButton onClick={zoomControls.resetTransform} size="small">
                <RestartAltIcon />
              </IconButton>
              <IconButton onClick={zoomControls.zoomIn} size="small">
                <ZoomInIcon />
              </IconButton>
            </>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        
        <DialogContent sx={{ 
          p: 0, 
          height: isVideo ? 'auto' : (isImage ? '80vh' : 'auto'),
          minHeight: isVideo ? '0' : '300px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          {renderMediaContent()}
        </DialogContent>
      </Dialog>
    );
  }

  // Preview/thumbnail mode - direct render
  return (
    <Box sx={{ 
      width: isVideo ? '85vw' : width,
      height: isVideo ? 'auto' : (isPdf ? 'auto' : height),
      minHeight: isVideo ? '0' : (isPdf ? '500px' : height),
      maxHeight: isPdf ? '80vh' : (isVideo ? '60vh' : undefined),
      aspectRatio: isVideo ? '16/9' : undefined,
      maxWidth: '100%',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {renderMediaContent()}
    </Box>
  );
});

export default MediaViewer; 